import { ENVIRONMENT, FIREBASE } from 'core/datasets/action';
import { getDatabase, child, ref, get, DataSnapshot, query, limitToLast, off } from 'firebase/database';
import { environmentSelectors } from '../../../environment/redux/environmentSelectors';
import { select, take, call, put } from 'redux-saga/effects';
import firebaseSelectors from '../../../firebase/redux/firebaseSelectors';
import * as yup from 'yup';
import { EventFirebase } from '../../../firebase/classes';
import { isNumber } from 'lodash';
import { firebaseActions } from '../../../firebase/redux/firebaseActions';

const channelDataSchema = yup
    .object({
        last_message: yup.string().nullable(),
        last_updated: yup.number().nullable(),
    })
    .nullable();

async function getSnapshotValue(dbKey: string): Promise<any> {
    const dbRef = ref(getDatabase());
    const result: DataSnapshot = await get(child(dbRef, dbKey));

    return result.val();
}

async function querySnapshotValue(dbKey: string): Promise<any> {
    const dbRef = ref(getDatabase());
    const result: DataSnapshot = await get(query(child(dbRef, dbKey), limitToLast(25)));

    return result.val();
}

export function unsubscribeChannel(dbKey: string): void {
    off(ref(getDatabase(), dbKey), EventFirebase.CHILD_ADDED);
}

export function* getChannelRef(channelId: string): Generator {
    try {
        const result = yield call(getSnapshotValue, `channels/${channelId}/meta`);
        const validData = channelDataSchema.validateSync(result);

        return validData;
    } catch (error) {
        console.error('Error fetching channel data:', error);
        return null;
    }
}

export function* getChannelLastActivity(channelId: string, firebaseUserUid: string): Generator {
    const lastActivity = yield call(getSnapshotValue, `channels/${channelId}/lastActivity/${firebaseUserUid}`);

    return lastActivity ? Number(lastActivity) : 0;
}

export function* getFirebaseUser(): Generator {
    let firebaseUser = yield select(firebaseSelectors.user);

    if (!firebaseUser) {
        yield take([FIREBASE.SET_FIREBASE_USER]);
        firebaseUser = yield select(firebaseSelectors.user);
    }

    return firebaseUser;
}

export function* getDisplayName(): Generator {
    let displayName = yield select(environmentSelectors.landLordName);

    if (!displayName) {
        yield take(ENVIRONMENT.SET_ENVIRONMENT_SETTINGS);
        displayName = yield select(environmentSelectors.landLordName);
    }

    return displayName;
}

export function* getMessageList(channelId: string): Generator {
    const messagesData = yield call(querySnapshotValue, `channels/${channelId}/messages`);

    return messagesData;
}

export function* getFirebaseToken(): Generator {
    let firebaseToken = yield select(firebaseSelectors.token);
    const expireAt = yield select(firebaseSelectors.expireAt);
    const isExpired = isNumber(expireAt) && expireAt > 0 && Date.now() >= expireAt * 1000;

    if (isExpired) {
        yield put(firebaseActions.getNewToken());
        yield take(FIREBASE.SET_TOKEN);
        firebaseToken = yield select(firebaseSelectors.token);
    }

    if (!firebaseToken) {
        yield take(FIREBASE.SET_TOKEN);
        firebaseToken = yield select(firebaseSelectors.token);
    }

    return firebaseToken;
}
